import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';
import { ApiErrorResponse } from 'src/app/shared/interfaces/api-error-response';

import {
  PostMerchantRequest,
  PostMerchantRootAdminUserRequest,
  SiteContextListResponse,
  SiteContextUpdateRequest,
  User,
} from '@app/api/marketplace/models';

export interface AuthenticationState {
  isLoading: boolean;
  merchantRegistrationRequest: PostMerchantRequest;
  merchantRegistrationResponse: User;
  merchantRegistrationErrorResponse?: ApiErrorResponse;
  merchantUserRegistrationRequest: PostMerchantRootAdminUserRequest;
  merchantUserRegistrationResponse: User;
  merchantUserRegistrationErrorResponse?: ApiErrorResponse;
  choosableContexts: Array<SiteContextListResponse>;
  switcherOpened: boolean;
  userInfo: any;
  hasSwitcherAppeared: boolean;
}

export const initialState: AuthenticationState = {
  isLoading: false,
  merchantRegistrationRequest: {
    companyName: '',
    brandName: '',
    taxNumber: '',
    email: '',
    phone: '',
    promotionCode: '',
  },
  merchantRegistrationResponse: {},
  merchantRegistrationErrorResponse: undefined,
  merchantUserRegistrationRequest: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    merchantId: '',
  },
  merchantUserRegistrationResponse: {
    firstName: '',
    lastName: '',
    email: '',
    merchantId: '',
  },
  merchantUserRegistrationErrorResponse: undefined,
  choosableContexts: [],
  switcherOpened: false,
  userInfo: {},
  hasSwitcherAppeared: false,
};

export const {
  actions: AuthenticationActions,
  selectors,
  ...AuthenticationFeature
} = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    resetState: () => ({
      ...initialState,
    }),
    refreshToken: {
      trigger: (state) => {
        return {
          ...state,
          isLoading: true,
        };
      },
      success: (state) => {
        return {
          ...state,
          isLoading: false,
        };
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
      }),
    },
    getChoosableContexts: {
      trigger: (state) => ({ ...state, isLoading: true }),
      success: (
        state,
        action: PayloadAction<{ response: Array<SiteContextListResponse> }>
      ) => ({
        ...state,
        isLoading: false,
        choosableContexts: action.response,
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
      }),
    },
    getUserInfo: {
      trigger: (state) => ({ ...state, isLoading: true }),
      success: (state, action: PayloadAction<{ response: any }>) => ({
        ...state,
        isLoading: false,
        userInfo: action.response,
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
      }),
    },
    toggleOpenedSwitcher: {
      trigger: (state, action: PayloadAction<{ opened: boolean }>) => ({
        ...state,
        switcherOpened: action.opened,
        hasSwitcherAppeared: true,
      }),
      success: () => {},
    },
    contextSwitch: {
      trigger: (
        state,
        action: PayloadAction<{ data: SiteContextUpdateRequest }>
      ) => {
        return {
          ...state,
          isLoading: true,
        };
      },
      success: (state) => {
        return {
          ...state,
          isLoading: false,
        };
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
        merchantRegistrationErrorResponse: action,
      }),
    },
    registerMerchant: {
      trigger: (
        state,
        action: PayloadAction<{ data: PostMerchantRequest }>
      ) => {
        return {
          ...state,
          isLoading: true,
          merchantRegistrationRequest: action.data,
        };
      },
      success: (state, action: PayloadAction<{ response: User }>) => {
        return {
          ...state,
          isLoading: false,
          merchantRegistrationResponse: action.response,
        };
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
        merchantRegistrationErrorResponse: action,
      }),
    },
    // Telesales step 1
    registerMerchantByTelesales: {
      trigger: (
        state,
        action: PayloadAction<{ data: PostMerchantRequest }>
      ) => ({
        ...state,
        isLoading: true,
        merchantRegistrationRequest: action.data,
      }),
      success: (state, action: PayloadAction<{ response: User }>) => ({
        ...state,
        isLoading: false,
        merchantRegistrationResponse: action.response,
      }),
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
        merchantRegistrationErrorResponse: action,
      }),
    },
    registerMerchantUser: {
      trigger: (
        state,
        action: PayloadAction<{ user: PostMerchantRootAdminUserRequest }>
      ) => {
        return {
          ...state,
          isLoading: true,
          merchantUserRegistrationRequest: action.user,
        };
      },
      success: (state, action: PayloadAction<{ user: User }>) => {
        return {
          ...state,
          isLoading: false,
          merchantUserRegistrationResponse: action.user,
        };
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
        merchantUserRegistrationErrorResponse: action,
      }),
    },
    // Telesales step 2
    registerMerchantUserByTelesales: {
      trigger: (
        state,
        action: PayloadAction<{ user: PostMerchantRootAdminUserRequest }>
      ) => {
        return {
          ...state,
          isLoading: true,
          merchantUserRegistrationRequest: action.user,
        };
      },
      success: (state, action: PayloadAction<{ user: User }>) => {
        return {
          ...state,
          isLoading: false,
          merchantUserRegistrationResponse: action.user,
        };
      },
      failure: (state, action: PayloadAction<ApiErrorResponse>) => ({
        ...state,
        isLoading: false,
        merchantUserRegistrationErrorResponse: action,
      }),
    },
  },
});

export const AuthenticationSelectors = {
  ...selectors,
  selectMerchantId: createSelector(
    selectors.selectMerchantRegistrationResponse,
    (state) => state.id ?? ''
  ),
};
